import React, { useState, useEffect } from 'react';
import { Head, useForm, router } from '@inertiajs/react';
import { Container, Paper, Group, TextInput, MultiSelect, Button, Loader, Stack } from '@mantine/core';
import { Notifications, showNotification } from '@mantine/notifications';
import { DatePickerInput } from '@mantine/dates';
import { RichTextEditor } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import LinkExtension from '@tiptap/extension-link';
import ImageUploadModal from '../../components/ImageUploadModal';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/tiptap/styles.css';

interface Props {
  post: {
    id: number;
    title: string;
    published_at: string | null;
    post_type_ids: number[];
    category_ids: number[];
    tag_ids: number[];
    dance_style_ids: number[];
    artist_ids: number[];
    body: string;
    podcast_id?: string;
    video_url?: string;
    image_url?: string;
    slug: string;
  };
  postTypes: { id: number; name: string }[];
  categories: { id: number; name: string }[];
  tags: { id: number; name: string }[];
  danceStyles: { id: number; name: string }[];
  artists: { id: number; name: string }[];
}

export default function EditPost({
  post,
  postTypes = [],
  categories = [],
  tags = [],
  danceStyles = [],
  artists = [],
}: Props) {
  // State for image preview URL
  const [previewUrl, setPreviewUrl] = useState<string | null>(post.image_url || null);

  // Inertia form setup
  const form = useForm<{
    title: string;
    published_at: string | null;
    post_type_ids: number[];
    category_ids: number[];
    tag_ids: number[];
    dance_style_ids: number[];
    artist_ids: number[];
    body: string;
    podcast_id: string;
    video_url: string;
    featured_image: File | null;
  }>({
    title: post.title,
    published_at: post.published_at,
    post_type_ids: post.post_type_ids,
    category_ids: post.category_ids,
    tag_ids: post.tag_ids,
    dance_style_ids: post.dance_style_ids,
    artist_ids: post.artist_ids,
    body: post.body,
    podcast_id: post.podcast_id || '',
    video_url: post.video_url || '',
    featured_image: null,
  });

  // Setup TipTap editor
  const editor = useEditor({
    extensions: [StarterKit, LinkExtension],
    content: form.data.body,
    onUpdate: ({ editor }) => {
      form.setData('body', editor.getHTML());
    },
  });

  // Determine whether to show podcast/video fields
  const podcastTypeId = postTypes.find((pt) => pt.name === 'Podcast')?.id;
  const videoTypeId = postTypes.find((pt) => pt.name === 'Video')?.id;
  const showPodcastField = podcastTypeId != null && form.data.post_type_ids.includes(podcastTypeId);
  const showVideoField = videoTypeId != null && form.data.post_type_ids.includes(videoTypeId);

  // Handler for image selection/cropping
  const handleImageChange = (file: File) => {
    setPreviewUrl(URL.createObjectURL(file));
    form.setData('featured_image', file);
  };

  // Submit handler: wrap data under `post` so Rails strong params pick up arrays
  function submit() {
    const csrf = document.querySelector('meta[name="csrf-token"]')!.getAttribute('content')!;
    const fd = new FormData();

    // ─── nest under post[...] ───
    fd.append('post[title]', form.data.title);
    fd.append('post[published_at]', form.data.published_at || '');
    form.data.post_type_ids.forEach((id) => fd.append('post[post_type_ids][]', String(id)));
    form.data.category_ids.forEach((id) => fd.append('post[category_ids][]', String(id)));
    form.data.tag_ids.forEach((id) => fd.append('post[tag_ids][]', String(id)));
    form.data.dance_style_ids.forEach((id) => fd.append('post[dance_style_ids][]', String(id)));
    form.data.artist_ids.forEach((id) => fd.append('post[artist_ids][]', String(id)));
    fd.append('post[body]', form.data.body);
    fd.append('post[podcast_id]', form.data.podcast_id);
    fd.append('post[video_url]', form.data.video_url);
    if (form.data.featured_image) {
      fd.append('post[featured_image]', form.data.featured_image);
    }

    // ─── debug: list out every FormData entry ───
    console.group('🪵 FormData to /posts/:id');
    for (let [key, val] of fd.entries()) {
      console.log(key, val);
    }
    console.groupEnd();

    // ─── actually send it ───
    fetch(`/posts/${post.slug}`, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': csrf,
        Accept: 'application/json',
      },
      body: fd,
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          showNotification({ color: 'green', message: 'Post updated!' });
          window.location.href = data.redirect_url;
        } else {
          showNotification({ color: 'red', message: data.errors.join(', ') });
        }
      })
      .catch((e) => {
        console.error('❌ fetch error', e);
        showNotification({ color: 'red', message: 'Unexpected error.' });
      });
  }

  return (
    <Container size="sm" mb="4rem" style={{ marginTop: '2rem', minHeight: '75vh' }}>
      <Head title="Edit Post" />
      <Notifications position="top-right" />
      <Paper p="md" shadow="sm" radius="md" style={{ backgroundColor: '#1a1b1e' }}>
        <Stack gap="lg">
          {/* Image Upload and Crop Modal */}
          <ImageUploadModal previewUrl={previewUrl} onImageChange={handleImageChange} aspectRatio={16 / 9} />

          <TextInput
            label="Title"
            withAsterisk
            value={form.data.title}
            onChange={(e) => form.setData('title', e.currentTarget.value)}
            error={form.errors.title}
          />

          <Group align="flex-start" gap={0}>
            <DatePickerInput
              label="Publish Date"
              placeholder="Pick a date"
              styles={{ input: { backgroundColor: '#f2f2f2', color: '#171717' }, label: { color: '#f2f2f2' } }}
              value={form.data.published_at ? new Date(form.data.published_at) : null}
              onChange={(date) => form.setData('published_at', date?.toISOString().slice(0, 10) || '')}
              maw={700}
              clearable
              mx="left"
            />
          </Group>

          <MultiSelect
            data={postTypes.map((pt) => ({ value: pt.id.toString(), label: pt.name }))}
            label="Post Types"
            placeholder="Select one or more"
            value={form.data.post_type_ids.map(String)}
            onChange={(vals) => form.setData('post_type_ids', vals.map(Number))}
            error={form.errors.post_type_ids}
          />

          <MultiSelect
            data={categories.map((c) => ({ value: c.id.toString(), label: c.name }))}
            label="Categories"
            value={form.data.category_ids.map(String)}
            onChange={(vals) => form.setData('category_ids', vals.map(Number))}
          />

          <MultiSelect
            data={tags.map((t) => ({ value: t.id.toString(), label: t.name }))}
            label="Tags"
            value={form.data.tag_ids.map(String)}
            onChange={(vals) => form.setData('tag_ids', vals.map(Number))}
          />

          <MultiSelect
            data={danceStyles.map((ds) => ({ value: ds.id.toString(), label: ds.name }))}
            label="Dance Styles"
            searchable
            clearable
            value={form.data.dance_style_ids.map(String)}
            onChange={(vals) => form.setData('dance_style_ids', vals.map(Number))}
          />

          <MultiSelect
            data={artists.map((a) => ({ value: a.id.toString(), label: a.name }))}
            label="Linked Artists"
            searchable
            clearable
            value={form.data.artist_ids.map(String)}
            onChange={(vals) => form.setData('artist_ids', vals.map(Number))}
          />

          {/* Conditionally render Podcast ID */}
          {showPodcastField && (
            <TextInput
              label="Podcast ID"
              placeholder="captivate.fm episode ID"
              value={form.data.podcast_id}
              onChange={(e) => form.setData('podcast_id', e.currentTarget.value)}
              error={form.errors.podcast_id}
            />
          )}

          {/* Conditionally render Video URL */}
          {showVideoField && (
            <TextInput
              label="Video URL"
              placeholder="YouTube or Vimeo URL"
              value={form.data.video_url}
              onChange={(e) => form.setData('video_url', e.currentTarget.value)}
              error={form.errors.video_url}
            />
          )}

          {/* RichTextEditor with toolbar */}
          {editor && (
            <RichTextEditor editor={editor} style={{ backgroundColor: '#fff' }}>
              <RichTextEditor.Toolbar>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Bold />
                  <RichTextEditor.Italic />
                  <RichTextEditor.Underline />
                  <RichTextEditor.Strikethrough />
                </RichTextEditor.ControlsGroup>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Code />
                  <RichTextEditor.Highlight />
                  <RichTextEditor.ClearFormatting />
                </RichTextEditor.ControlsGroup>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Link />
                  <RichTextEditor.Unlink />
                </RichTextEditor.ControlsGroup>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.H1 />
                  <RichTextEditor.H2 />
                  <RichTextEditor.H3 />
                </RichTextEditor.ControlsGroup>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Blockquote />
                  <RichTextEditor.Hr />
                  <RichTextEditor.BulletList />
                  <RichTextEditor.OrderedList />
                </RichTextEditor.ControlsGroup>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Undo />
                  <RichTextEditor.Redo />
                </RichTextEditor.ControlsGroup>
              </RichTextEditor.Toolbar>
              <RichTextEditor.Content />
            </RichTextEditor>
          )}

          <Group justify="right">
            <Button onClick={submit} mt="2rem" size="lg" fullWidth color="orange" disabled={form.processing}>
              {form.processing ? <Loader size="xs" color="orange" /> : 'Update Post'}
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Container>
  );
}
